import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Track } from "../data/sharedTypes";
import {
    getImDisciplinesForInstitution,
    getImInstitutionsForTrack,
    imInstitutions,
    type ImInstitution,
} from "../data/internalMissionsData";
import StepHeader from "../StepHeader";

interface Props {
    universityNameKey: string;
    initialTrack?: Track | null;
    onBack: () => void;
    onSelectInstitution: (institution: ImInstitution, track: Track) => void;
    onTrackChange?: (track: Track) => void;
}

/**
 * Internal Missions — institution picker (IM-only step).
 *
 * Track tabs (Scientific / Literary) gate which institutions appear:
 *   - Scientific shows every institution with ≥1 scientific row.
 *   - Literary hides institutions whose only rows are scientific
 *     (e.g. KCST has no literary rows in the 2026 PDF).
 */
export default function ChooseImInstitution({
    universityNameKey,
    initialTrack,
    onBack,
    onSelectInstitution,
    onTrackChange,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [activeTrack, setActiveTrack] = useState<Track>(initialTrack ?? "scientific");

    const handleTrackChange = (track: Track) => {
        setActiveTrack(track);
        onTrackChange?.(track);
    };

    const visibleInstitutions = getImInstitutionsForTrack(activeTrack);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_im_choose_institution")}
                subtitle={t(universityNameKey)}
            />

            {/* Track tabs */}
            <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
                {(["scientific", "literary"] as Track[]).map((track) => (
                    <button
                        key={track}
                        onClick={() => handleTrackChange(track)}
                        className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                            activeTrack === track
                                ? "bg-white text-theme-700 shadow-sm"
                                : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {t(`dream_university_track_${track}`)}
                    </button>
                ))}
            </div>

            {/* Institution grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {visibleInstitutions.map((institution) => {
                    const disciplineCount = getImDisciplinesForInstitution(
                        institution.key,
                        activeTrack,
                    ).length;
                    return (
                        <button
                            key={institution.key}
                            onClick={() => onSelectInstitution(institution, activeTrack)}
                            className="flex flex-col items-center gap-2 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200"
                        >
                            {institution.logo ? (
                                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white ring-1 ring-gray-200 overflow-hidden p-1.5">
                                    <img
                                        src={institution.logo}
                                        alt={t(`dream_university_institution_${institution.key}`)}
                                        className="w-full h-full object-contain"
                                        loading="lazy"
                                    />
                                </span>
                            ) : (
                                <span
                                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-50 ring-1 ring-gray-200 overflow-hidden text-3xl leading-none"
                                    aria-hidden="true"
                                >
                                    {institution.emoji}
                                </span>
                            )}
                            <span className="text-sm font-semibold text-gray-800 text-center leading-tight">
                                {t(`dream_university_institution_${institution.key}`)}
                            </span>
                            <span className="text-[10px] text-gray-500">
                                {t("dream_university_im_college_count", { count: disciplineCount })}
                            </span>
                        </button>
                    );
                })}
            </div>

            {visibleInstitutions.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_im_no_institutions_for_track")}
                </p>
            )}

            {/* Keep registry reference so unused-import warnings don't trim it. */}
            <span className="hidden">{imInstitutions.length}</span>
        </div>
    );
}
